import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/system/Unstable_Grid';
import { Card, Stack, Button, CardHeader, Typography, CardContent } from '@mui/material';

import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SigningOriginType } from 'src/services/signing/signing.types';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { useGetContactByIdQuery, useGetContactActivitiesQuery, useGetContactCompanyLinksQuery } from 'src/services/contacts/contacts.service';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import LinkedInLink, { linkedinUrlFromLinks } from 'src/components/linkedin-link';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import RecordActionMenuItems from 'src/components/record-panel/record-action-menu-items';

import ActivityFeed from 'src/sections/activity/card/activity-feed';
import SigningDocumentsList from 'src/sections/signing/signing-documents-list';
import { EntityListsCard } from 'src/sections/lists/components/entity-lists-card';
import AccountOwnerCard from 'src/sections/contacts/components/account-owner-card';
import { ContactDetailSection } from 'src/sections/contacts/components/detail-card';
import { useContactActions } from 'src/sections/contacts/hooks/use-contact-actions';
import CandidateLinkCard from 'src/sections/contacts/components/candidate-link-card';
import ContactDocumentsView from 'src/sections/contacts/components/contact-documents';
import { ContactProfileSection } from 'src/sections/contacts/components/profile-card';
import { LinkedDealsCard } from 'src/sections/deal-flow/components/linked-deals-card';
import LinkedCompaniesCard from 'src/sections/contacts/components/linked-companies-card';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import CommunicationActions from 'src/sections/admin/integrations/communications/comms-actions';
import { buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';
import { ContactInheritedLabelsCard } from 'src/sections/contacts/components/contact-inherited-labels-card';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum ContactsTab {
  DETAIL = 'detail',
  PROFILE = 'profile',
  DOCUMENTS = 'documents',
}

export default function ContactsDetailView({ overrideContactId }: { overrideContactId?: string } = {}) {

  const { t } = useTranslate();

  // `overrideContactId` lets the record overlay (JT1-1529) embed this view without a route param.
  const { contact_id: contactIdParam } = useParams();
  const contact_id = overrideContactId ?? contactIdParam;

  const optionsPopover = usePopover();

  const [currentTab, setCurrentTab] = useState<ContactsTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const router = useRouter();

  const tenantType = useOrgTenant();

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: ContactsTab = ContactsTab.DETAIL

    // Embedded in the record overlay, ?tab belongs to the host page, not to this contact. The
    // Companies page writes ?tab=companies, which used to match a ContactsTab value with no tab or
    // body, so a contact opened from there showed an empty profile until a tab was clicked.
    const existingTab = overrideContactId ? null : searchParams.get('tab');
    if (existingTab && Object.values(ContactsTab).includes(existingTab as ContactsTab)) {
      defaultTab = existingTab as ContactsTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams, overrideContactId]);

  const [activitiesPage, setActivitiesPage] = useState({ page: 1, per_page: 10 });

  const { currentData: contact } = useGetContactByIdQuery(contact_id as string, {
    skip: !contact_id,
  });

  const { data: activities } = useGetContactActivitiesQuery({
    contact_id: contact_id as string,
    page: activitiesPage.page,
    perPage: activitiesPage.per_page,
  }, { skip: !contact_id });

  const { data: contactCompanyLinks } = useGetContactCompanyLinksQuery({
    contact_id: contact_id as string,
    params: DEFAULT_PAGE,
  }, {
    skip: !contact_id,
  });

  // Every contact action, shared with the quick view and the list toolbar (JT1-1799). Call and SMS stay
  // as the icon buttons beside the Actions button, so they're left out of the menu.
  const { actions: contactActions, modals: contactActionModals } = useContactActions({
    mode: 'record',
    ids: contact_id ? [contact_id] : [],
    exclude: ['call', 'send-sms'],
  });

  const linkedCompanyName = contactCompanyLinks?.results?.[0]?.company_profile?.company_name ?? contact?.company_name;

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: ContactsTab) => {
    setCurrentTab(newValue);
  }, []);


  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    // Embedded in the record overlay (JT1-1529): the tab is local state; writing it to the URL would
    // wipe the co-owned ?preview params and close the overlay. Only sync on the standalone page.
    if (overrideContactId) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams, overrideContactId]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="column" justifyContent="start" alignItems="start" spacing={1}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <TruncatedTextField
                // Shown exactly as entered: capitalising turned "BT Dialpad" into "Bt Dialpad".
                text={contact ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() : "Loading..."}
                limit={40}
                mode='characters'
                variant="h3"
                sx={{ mb: 0, lineHeight: 1 }}
                showMoreEnabled={false}
              />
              {/* One-click through to LinkedIn (JT1-1480). */}
              <LinkedInLink url={linkedinUrlFromLinks(contact?.links)} size={20} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="start" justifyContent="start" width="100%">

              {
                linkedCompanyName && (
                  <Box>
                    <TruncatedTextField
                      text={linkedCompanyName}
                      limit={40}
                      mode='characters'
                      variant="subtitle2"
                      showMoreEnabled={false}
                    />
                  </Box>
                )
              }

              {
                contact?.position_title && (
                  <>
                    {linkedCompanyName && <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>-</Typography>}
                    <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>{contact.position_title}</Typography>
                  </>
                )
              }

              {/* Read-only contact location (JT1-1540). */}
              {
                contact?.location && (
                  <>
                    {(linkedCompanyName || contact?.position_title) && <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>-</Typography>}
                    <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>{contact.location}</Typography>
                  </>
                )
              }

            </Stack>

          </Stack>
        }
        action={
          <Stack direction="row" spacing={.5} alignItems="center" mt={2}>

            <CommunicationActions
              phoneNumber={buildDialNumber(contact?.phone)}
              name={contact ? `${contact.first_name} ${contact.last_name}` : undefined}
              entity={contact ? { entity_type: CommunicationEntityType.CONTACT, entity_id: contact.id } : undefined}
            />

            <Button
              variant="outlined"
              endIcon={<Iconify icon="carbon:overflow-menu-horizontal" />}
              sx={{ mr: 1 }}
              onClick={optionsPopover.onOpen}
            >
              {t('common.actions')}
            </Button>

          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>

      <Tab
        key={ContactsTab.DETAIL}
        iconPosition="end"
        value={ContactsTab.DETAIL}
        label={t('contacts.detail.tabs.detail')}
      />

      <Tab
        key={ContactsTab.PROFILE}
        iconPosition="end"
        value={ContactsTab.PROFILE}
        label={t('contacts.detail.tabs.profile')}
      />

      <Tab
        key={ContactsTab.DOCUMENTS}
        iconPosition="end"
        value={ContactsTab.DOCUMENTS}
        label={t('contacts.detail.tabs.documents')}
      />

    </Tabs>
  );

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      <Grid container spacing={2}>
        <Grid xs={12} md={8}>
          {currentTab === ContactsTab.PROFILE && (
            <ContactProfileSection contactId={contact_id as string} />
          )}

          {currentTab === ContactsTab.DETAIL && (
            <Stack spacing={2}>
              <ContactDetailSection contactId={contact_id as string} />
              <Card>
                <CardHeader title={t('admin-connections.detail.activities')} />
                <CardContent>
                  <ActivityFeed
                    activities={activities?.results ?? []}
                    page={activitiesPage.page}
                    perPage={activitiesPage.per_page}
                    onPageChange={(_e, newPage) => setActivitiesPage({ ...activitiesPage, page: newPage })}
                    totalItems={activities?.count ?? 0}
                  />
                </CardContent>
              </Card>
            </Stack>
          )}

          {currentTab === ContactsTab.DOCUMENTS && (
            <Stack spacing={3}>
              <ContactDocumentsView contactId={contact_id as string} />
              <SigningDocumentsList originType={SigningOriginType.CONTACT} originId={contact_id as string} />
            </Stack>
          )}
        </Grid>
        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>

            <AccountOwnerCard contactId={contact_id as string} />

            <CandidateLinkCard contactId={contact_id as string} />

            <LinkedCompaniesCard contactId={contact_id as string} />

            {/* JT1-1736: the deals this contact is linked to */}
            <LinkedDealsCard contactId={contact_id as string} />

            <LinkedContactsCard entityId={contact_id as string} entityType={ContactLinkEntityType.CONTACT} />

            {/* JT1-1276 Phase 5: lists this contact belongs to (below Linked) */}
            <EntityListsCard entityType={ListEntityType.CONTACT} entityId={contact_id as string} />

            {/* Read-only labels inherited from the linked company (JT1-1541 QA). */}
            <ContactInheritedLabelsCard
              labels={contactCompanyLinks?.results?.[0]?.company_profile?.labels}
              companyName={linkedCompanyName}
            />

          </Stack>

        </Grid>
      </Grid>
      <CustomPopover
        open={optionsPopover.open}
        onClose={optionsPopover.onClose}
        arrow="top-right"
      >
        <RecordActionMenuItems actions={contactActions} onClose={optionsPopover.onClose} />
      </CustomPopover>

      {contactActionModals}

    </>
  );
}
